import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Star } from "lucide-react";

function Stars({ n, size }) {
  return (
    <div className="flex gap-1" aria-label={`${n} dari 5 bintang`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={1.6}
          className={i <= n ? "text-[#FF6B1A] fill-[#FF6B1A]" : "text-[#D2D2D7]"}
        />
      ))}
    </div>
  );
}

function Kartu({ u, mobile }) {
  return (
    <article
      className="bg-white border border-[#E5E5EA] rounded-[2px] p-6 md:p-8 flex flex-col gap-4 transition-all duration-300 hover:-translate-y-1"
      style={{ boxShadow: "0 10px 15px -3px rgba(0,0,0,.05), 0 4px 6px -4px rgba(0,0,0,.05)" }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 25px 50px -12px rgba(0,0,0,.12)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0,0,0,.05), 0 4px 6px -4px rgba(0,0,0,.05)"; }}
      data-testid={`ulasan-card-${u.id}`}
    >
      <Stars n={u.bintang} size={mobile ? 22 : 24} />
      <p className="text-[15px] leading-[1.6] text-[#6E6E73]">{u.teks}</p>
      <div>
        <div className="text-[15px] font-semibold text-[#1D1D1F]">{u.nama}</div>
        {u.kota && <div className="text-[13px] text-[#6E6E73]">{u.kota}</div>}
      </div>
    </article>
  );
}

function Kolom({ items, kecepatan, areaH, mobile, tid }) {
  const ref = useRef(null);
  const [gulir, setGulir] = useState(false);
  const [durasi, setDurasi] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cek = () => {
      // Jarak satu putaran = tinggi satu salinan + jarak antar kartu (gap 24px).
      const jarak = el.scrollHeight + 24;
      setGulir(el.scrollHeight > areaH + 8);
      setDurasi(jarak / Math.max(1, kecepatan));
    };
    cek();
    window.addEventListener("resize", cek);
    return () => window.removeEventListener("resize", cek);
  }, [items, areaH, kecepatan]);

  const konten = (
    <div ref={ref} className="flex flex-col gap-6">
      {items.map((u) => <Kartu key={u.id} u={u} mobile={mobile} />)}
    </div>
  );

  if (!gulir) {
    return <div className="flex flex-col justify-center" data-testid={tid}>{konten}</div>;
  }
  return (
    <div className="hsv5-fade-mask overflow-hidden" style={{ height: areaH }} data-testid={tid}>
      <div className="hsv5-marquee flex flex-col gap-6" style={{ animationDuration: `${durasi}s` }}
        data-kecepatan={kecepatan}>
        {konten}
        <div className="flex flex-col gap-6" aria-hidden="true">
          {items.map((u) => <Kartu key={`dup-${u.id}`} u={u} mobile={mobile} />)}
        </div>
      </div>
    </div>
  );
}

// Piksel per detik — bukan durasi tetap, supaya kecepatan tidak berubah saat
// jumlah ulasan bertambah. Kolom 2 & 3 di desktop dibedakan ±10% agar tidak seragam.
const PX_PER_DETIK = { LAMBAT: 15, SEDANG: 25, CEPAT: 40 };

export function Testimonials({ ulasan, kecepatan }) {
  const [mobile, setMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  useEffect(() => {
    const cek = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", cek);
    return () => window.removeEventListener("resize", cek);
  }, []);

  const list = ulasan || [];
  if (!list.length) return null;

  const kolom = mobile ? [list] : [0, 1, 2].map((k) => list.filter((_, i) => i % 3 === k)).filter((c) => c.length);
  const dasar = PX_PER_DETIK[String(kecepatan || "LAMBAT").toUpperCase()] || PX_PER_DETIK.LAMBAT;
  const laju = mobile ? [dasar] : [dasar, dasar * 0.9, dasar * 1.1];
  const areaH = mobile ? 460 : 560;

  return (
    <section className="py-6" data-testid="testimonials">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-0">
        <h2 className="text-center text-[34px] md:text-5xl font-bold leading-[1.05] tracking-[-0.045em] text-[#1D1D1F]" data-testid="testimonials-title">
          Apa Kata Pelanggan Kami
        </h2>
        <p className="mt-[10px] md:mt-3 text-center text-base md:text-lg text-[#6E6E73]">
          Lihat apa kata pelanggan tentang HoonStore.
        </p>
        <div className="mt-6 md:mt-7 flex justify-center gap-6">
          {kolom.map((items, i) => (
            <div key={i} className="w-full md:w-[360px] md:flex-1 md:min-w-0 max-w-[360px] shrink md:shrink" style={mobile ? { width: "min(360px, 100vw - 32px)" } : undefined}>
              <Kolom items={items} kecepatan={laju[i] ?? dasar} areaH={areaH} mobile={mobile} tid={`ulasan-kolom-${i}`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
